import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useThree } from "@react-three/fiber";
import { nanoid } from "nanoid";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Vector2 } from "three";
const OverlayApiContext = createContext(null);
const OverlayContainersContext = createContext(null);
const OverlayBumpContext = createContext(null);
export const OverlayContextProvider = ({ children }) => {
    const containers = useRef(Object.create(null));
    const [bumpState, setBumpState] = useState({});
    const overlayApi = useMemo(() => ({
        setContainer: (id, node) => {
            containers.current[id] = node;
            setBumpState({});
        },
        deleteContainer: (id) => {
            delete containers.current[id];
            setBumpState({});
        }
    }), [containers, setBumpState]);
    return _jsx(OverlayApiContext.Provider, { value: overlayApi, children: _jsx(OverlayContainersContext.Provider, { value: containers.current, children: _jsx(OverlayBumpContext.Provider, { value: bumpState, children: children }) }) });
};
export const OverlayRoot = () => {
    const containers = useContext(OverlayContainersContext);
    const bump = useContext(OverlayBumpContext);
    return _jsx("div", { children: Object.values(containers) });
};
export const OverlayContainer = (props) => {
    const { children, apiRef, sizeApi, deps } = props;
    const containerElementRef = useRef(null);
    const idRef = useRef(nanoid());
    const wrappedChildren = useMemo(() => {
        return _jsx("div", { style: { position: 'absolute', zIndex: 50 }, ref: containerElementRef, children: children }, idRef.current);
    }, [children]);
    const overlayApi = useContext(OverlayApiContext);
    useLayoutEffect(() => {
        overlayApi.setContainer(idRef.current, wrappedChildren);
        return () => {
            overlayApi.deleteContainer(idRef.current);
        };
    }, deps);
    useEffect(() => {
        if (apiRef !== undefined) {
            apiRef.current = {
                setPosition: (x, y) => {
                    if (containerElementRef.current !== null) {
                        containerElementRef.current.style.top = y;
                        containerElementRef.current.style.left = x;
                    }
                },
                getWidth: () => {
                    return containerElementRef.current === null ? 0 : containerElementRef.current.clientWidth;
                },
                setSize: (scaleFactor) => {
                    sizeApi === null || sizeApi === void 0 ? void 0 : sizeApi(scaleFactor);
                }
            };
        }
        return () => {
            if (apiRef !== undefined) {
                apiRef.current = undefined;
            }
        };
    }, []);
    return _jsx(_Fragment, {});
};
export const ThreeJSOverlayContainer = (props) => {
    const { apiRef } = props;
    const innerApiRef = useRef(undefined);
    const { size: canvasSize, camera, } = useThree();
    useEffect(() => {
        if (apiRef !== undefined) {
            apiRef.current = {
                setPosition: (position) => {
                    if (innerApiRef.current === undefined) {
                        return;
                    }
                    const widthHalf = canvasSize.width / 2;
                    const heightHalf = canvasSize.height / 2;
                    if (position instanceof Vector2) {
                        const x = canvasSize.left + widthHalf + position.x * widthHalf - innerApiRef.current.getWidth() / 2;
                        const y = canvasSize.top + heightHalf - position.y * heightHalf;
                        innerApiRef.current.setPosition(`${x}px`, `${y}px`);
                    }
                    else {
                        const pos = position.clone();
                        pos.project(camera);
                        const cameraDist = position.distanceTo(camera.position);
                        innerApiRef.current.setSize(1 / cameraDist);
                        const x = canvasSize.left + widthHalf + pos.x * widthHalf - innerApiRef.current.getWidth() / 2;
                        const y = canvasSize.top + heightHalf - pos.y * heightHalf;
                        innerApiRef.current.setPosition(`${x}px`, `${y}px`);
                    }
                }
            };
        }
    }, []);
    return _jsx(OverlayContainer, Object.assign({}, props, { apiRef: innerApiRef }));
};
//# sourceMappingURL=OverlayContainer.js.map